import { useState, useEffect } from 'react';
import SEO from '../../components/base/SEO';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Proform Chimney Systems',
    description: 'Leading the way in innovative chimney solutions. Proform Chimney Systems is an innovative and reliable chimney manufacturing company based in Turkey.',
    url: `${siteUrl}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: 'Proform Chimney Systems',
      description: 'Innovative and reliable chimney manufacturing company based in Turkey providing highest quality chimney systems for residential and commercial use.',
      foundingLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Istanbul',
          addressCountry: 'TR'
        }
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Akçaburgaz Mah. 3028 Sk. No:1 İç Kapı No:27',
        addressLocality: 'Esenyurt',
        addressRegion: 'Istanbul',
        addressCountry: 'TR'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+90-543-457-61-96',
        contactType: 'Customer Service',
        email: 'info@proformbaca.com'
      }
    }
  };

  // Statistics counter animation
  const [stats, setStats] = useState({
    projects: 0,
    experience: 0,
    countries: 0,
    certificates: 0
  });

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = {
      projects: 500,
      experience: 15,
      countries: 3,
      certificates: 6
    };

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setStats({
        projects: Math.floor(targets.projects * progress),
        experience: Math.floor(targets.experience * progress),
        countries: Math.floor(targets.countries * progress),
        certificates: Math.floor(targets.certificates * progress)
      });

      if (currentStep >= steps) {
        setStats(targets);
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEO
        title="About Proform Chimney Systems - Leading Chimney Manufacturer in Turkey"
        description="Proform Chimney Systems is an innovative and reliable chimney manufacturing company in Turkey. Years of experience providing highest quality chimney systems for residential and commercial use."
        keywords="about Proform, chimney manufacturer Turkey, chimney company Istanbul, industrial chimney, residential chimney solutions"
        canonical={`${siteUrl}/about`}
        schema={schema}
      />
      <div className="min-h-screen bg-white pt-20">
        {/* Hero Section */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/939beb1c58da4828dc1023c3d50b5da2.jpg"
              alt="About Hero"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          </div>
          
          <div className="relative z-10 text-center text-white px-6">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">About Us</h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto font-light">
              Pioneer in innovative chimney solutions
            </p>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="py-20 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-white mb-2">
                  {stats.projects}+
                </div>
                <div className="text-lg md:text-xl text-white/90 font-light">Completed Projects</div>
              </div>
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-white mb-2">
                  {stats.experience}+
                </div>
                <div className="text-lg md:text-xl text-white/90 font-light">Years of Experience</div>
              </div>
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-white mb-2">
                  {stats.countries}
                </div>
                <div className="text-lg md:text-xl text-white/90 font-light">Countries Exported</div>
              </div>
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-white mb-2">
                  {stats.certificates}
                </div>
                <div className="text-lg md:text-xl text-white/90 font-light">ISO Certificates</div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  Proform Chimney Systems
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Turkey's Trusted Chimney Manufacturer
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Proform Chimney Systems is an innovative and reliable chimney manufacturing company based in Turkey. Our mission is to combine the latest technology with traditional craftsmanship to provide the highest quality chimney systems for residential and commercial use.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  With years of experience in the industry, we have established ourselves as a trusted partner for architects, contractors, and homeowners seeking superior chimney solutions. Our commitment to excellence and customer satisfaction drives everything we do.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  We specialize in a wide range of chimney systems, from residential heating solutions to industrial-grade factory chimneys. We ensure that every project meets the highest standards of quality, safety, and efficiency.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="/services"
                    className="inline-block bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    Our Services
                  </a>
                  <a
                    href="/contact"
                    className="inline-block bg-gray-900 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/0d37c1b42ded3733bc132784c4b202cc.jpeg"
                    alt="Proform Facility"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-orange-500/10 rounded-3xl -z-10"></div>
                <div className="absolute -top-8 -left-8 w-64 h-64 bg-gray-900/5 rounded-3xl -z-10"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Timeline */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Company History</h2>
              <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-orange-200"></div>

              {/* Timeline items */}
              <div className="space-y-12">
                {/* Item 1 */}
                <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="md:text-right">
                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                      <div className="text-orange-500 font-bold text-2xl mb-2">2008</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Foundation</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Proform Chimney Systems was founded in Istanbul as a small workshop. In our early years, we focused on local residential projects.
                      </p>
                    </div>
                  </div>
                  <div className="hidden md:block"></div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-orange-500 rounded-full border-4 border-white shadow-lg"></div>
                </div>

                {/* Item 2 */}
                <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="hidden md:block"></div>
                  <div>
                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                      <div className="text-orange-500 font-bold text-2xl mb-2">2012</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Expansion</h3>
                      <p className="text-gray-700 leading-relaxed">
                        We increased our production capacity and began serving commercial projects. We received our first ISO certification.
                      </p>
                    </div>
                  </div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-orange-500 rounded-full border-4 border-white shadow-lg"></div>
                </div>

                {/* Item 3 */}
                <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="md:text-right">
                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                      <div className="text-orange-500 font-bold text-2xl mb-2">2016</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">International Growth</h3>
                      <p className="text-gray-700 leading-relaxed">
                        We entered the European market and made our first exports. We began manufacturing industrial chimney systems.
                      </p>
                    </div>
                  </div>
                  <div className="hidden md:block"></div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-orange-500 rounded-full border-4 border-white shadow-lg"></div>
                </div>

                {/* Item 4 */}
                <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="hidden md:block"></div>
                  <div>
                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                      <div className="text-orange-500 font-bold text-2xl mb-2">2024</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Market Leader</h3>
                      <p className="text-gray-700 leading-relaxed">
                        We became one of Turkey's leading chimney manufacturers. We have successfully completed over 500 projects and export to 3 countries.
                      </p>
                    </div>
                  </div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-orange-500 rounded-full border-4 border-white shadow-lg"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Values</h2>
              <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                The core values that shape the way we do business
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-white p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-shield-check-fill text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Quality</h3>
                <p className="text-gray-700 leading-relaxed text-center">
                  We never compromise on quality. Every product we make meets the highest industry standards and exceeds customer expectations.
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-lightbulb-flash-fill text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Innovation</h3>
                <p className="text-gray-700 leading-relaxed text-center">
                  Continuous innovation drives our product development. We integrate the latest technologies and sustainable practices.
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-customer-service-2-fill text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Service</h3>
                <p className="text-gray-700 leading-relaxed text-center">
                  Exceptional customer service and long-term support ensure our customers have the best experience from start to finish.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Proform?</h2>
              <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-lg text-white/80 max-w-3xl mx-auto">
                The features that set us apart in the industry
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-team-fill text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Expert Team</h3>
                <p className="text-white/70 leading-relaxed">
                  Our experienced professionals bring decades of combined expertise in chimney design and manufacturing.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-tools-fill text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Advanced Technology</h3>
                <p className="text-white/70 leading-relaxed">
                  State-of-the-art manufacturing facilities ensure precision and consistency in every product.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-leaf-fill text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Eco-Friendly</h3>
                <p className="text-white/70 leading-relaxed">
                  We are committed to environmental sustainability through clean energy solutions and responsible manufacturing.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-time-fill text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">On-Time Delivery</h3>
                <p className="text-white/70 leading-relaxed">
                  Reliable project timelines and efficient logistics ensure your chimney systems arrive on schedule.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-global-fill text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Global Reach</h3>
                <p className="text-white/70 leading-relaxed">
                  We export from Turkey to 3 countries and manufacture in compliance with international standards.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-medal-fill text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">ISO Certified</h3>
                <p className="text-white/70 leading-relaxed">
                  With 6 ISO certificates, we meet standards in quality management, safety, and customer satisfaction.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready for Your Project?
            </h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Our expert team is ready to offer customized chimney solutions for you. Get in touch now and let's bring your project to life together.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="/contact"
                className="inline-block bg-white text-orange-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line mr-2"></i>
                Contact Us
              </a>
              <a
                href="https://wa.me/905434576196"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gray-900 text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}